/**
 * SQLite Table Explorer for Browser Mode
 *
 * Implements table explorer functionality (fetchRows, metadata, edit mode) using sql.js.
 */

import type {
  TableExplorerFetchRowsRequest,
  TableExplorerFetchRowsResult,
  TableExplorerPreviewSelectRequest,
  TableExplorerPreviewSelectResult,
  TableExplorerPreviewChangesRequest,
  TableExplorerPreviewChangesResult,
  TableExplorerApplyChangesRequest,
  TableExplorerApplyChangesResult,
  TableExplorerFetchInboundForeignKeysRequest,
  TableExplorerFetchInboundForeignKeysResult,
  TableExplorerInboundForeignKey,
} from '../../types/tableExplorer';
import type {
  FilterGroup,
  SortDefinition,
  TableExplorerColumn,
  PendingChange,
  PendingInsert,
  PendingUpdate,
  PendingDelete,
} from '../../features/workspace/core/types/workspaceTypes';
import { containsLikeSql } from '../../features/workspace/common/utils/containsLikeSql';
import { getCurrentDatabase, getCurrentDatabaseId, isDatabaseOpen, exportCurrentDatabase } from './sqliteManager';
import { checkpointDatabase, AUTO_CHECKPOINT_THRESHOLD } from './demoDatabaseLoader';

/**
 * Escape a SQLite identifier (table/column name)
 */
function escapeIdentifier(name: string): string {
  return `"${name.replace(/"/g, '""')}"`;
}

/**
 * Escape a string value for SQLite
 */
function escapeString(value: string): string {
  return `'${value.replace(/'/g, "''")}'`;
}

/**
 * Convert a filter value to SQLite SQL
 */
function formatFilterValue(value: unknown): string {
  if (value === null || value === undefined) {
    return 'NULL';
  }
  if (typeof value === 'string') {
    return escapeString(value);
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (value instanceof Date) {
    return escapeString(value.toISOString());
  }
  return escapeString(String(value));
}

/**
 * Build WHERE clause from filter groups
 */
function buildWhereClause(filters: FilterGroup[]): string {
  if (filters.length === 0) return '';

  const groupClauses = filters.map((group) => {
    const conditions = group.conditions.map((condition) => {
      const column = escapeIdentifier(group.field);
      const { operator, value } = condition;

      switch (operator) {
        case '=':
        case '!=':
        case '>':
        case '>=':
        case '<':
        case '<=':
          return `${column} ${operator === '!=' ? '<>' : operator} ${formatFilterValue(value)}`;
        case 'LIKE':
          return `${column} LIKE ${formatFilterValue(value)}`;
        case 'CONTAINS':
          // The desktop emitters' contains: the value matched literally.
          return containsLikeSql(column, value, 'sqlite', formatFilterValue);
        case 'BETWEEN': {
          // Check if condition has value2 for BETWEEN
          const condition2 = condition as { value2?: unknown };
          if (condition2.value2 !== undefined) {
            return `${column} BETWEEN ${formatFilterValue(value)} AND ${formatFilterValue(condition2.value2)}`;
          }
          return `${column} = ${formatFilterValue(value)}`;
        }
        case 'IS NULL':
          return `${column} IS NULL`;
        case 'IS NOT NULL':
          return `${column} IS NOT NULL`;
        case 'IN':
        case 'NOT IN': {
          // "in list" / "not in list"; an empty list applies nothing.
          const list = Array.isArray(value) ? value : [];
          return list.length === 0 ? '' : `${column} ${operator} (${list.map(formatFilterValue).join(', ')})`;
        }
        default:
          return `${column} = ${formatFilterValue(value)}`;
      }
    }).filter((sql) => sql !== '');

    // OR within a group
    return conditions.length > 1 ? `(${conditions.join(' OR ')})` : (conditions[0] ?? '');
  }).filter((clause) => clause !== '');
  if (groupClauses.length === 0) return '';

  // AND between groups
  return `WHERE ${groupClauses.join(' AND ')}`;
}

/**
 * Build ORDER BY clause from sorting definitions
 */
function buildOrderByClause(sorting: SortDefinition[]): string {
  if (sorting.length === 0) return '';

  const orderParts = sorting.map((sort) => {
    const column = escapeIdentifier(sort.field);
    const direction = sort.direction === 'desc' ? 'DESC' : 'ASC';
    return `${column} ${direction}`;
  });

  return `ORDER BY ${orderParts.join(', ')}`;
}

/**
 * Build a SELECT SQL statement for preview (without executing)
 */
function buildSelectSql(
  tableName: string,
  columns: string[] | '*',
  filters: FilterGroup[],
  sorting: SortDefinition[],
  limit: number,
  offset: number
): string {
  const columnList =
    columns === '*'
      ? '*'
      : columns.map(escapeIdentifier).join(', ');

  const whereClause = buildWhereClause(filters);
  const orderByClause = buildOrderByClause(sorting);

  return `SELECT ${columnList}
FROM ${escapeIdentifier(tableName)}${whereClause ? '\n' + whereClause : ''}${orderByClause ? '\n' + orderByClause : ''}
LIMIT ${limit}
OFFSET ${offset};`;
}

/**
 * Preview SELECT query by generating SQL without executing.
 * This allows users to see the exact query that would be run.
 */
export async function previewSelect(
  request: TableExplorerPreviewSelectRequest
): Promise<TableExplorerPreviewSelectResult> {
  try {
    const { table, columns, limit, offset = 0, filters, sorting } = request;

    const sql = buildSelectSql(
      table.tableName,
      columns,
      filters,
      sorting,
      limit,
      offset
    );

    return {
      success: true,
      sql,
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to generate SELECT preview',
    };
  }
}

/**
 * Get column metadata from PRAGMA table_info
 */
function getColumnMetadata(tableName: string): TableExplorerColumn[] {
  const db = getCurrentDatabase();
  if (!db) return [];

  try {
    const results = db.exec(`PRAGMA table_info(${escapeIdentifier(tableName)})`);
    if (results.length === 0 || results[0].values.length === 0) {
      return [];
    }

    // PRAGMA table_info returns: cid, name, type, notnull, dflt_value, pk
    return results[0].values.map((row) => ({
      name: String(row[1]),
      dataType: String(row[2]) || 'TEXT',
      nullable: row[3] === 0,
      isPrimaryKey: row[5] === 1,
      isForeignKey: false, // SQLite FK detection would need additional PRAGMA
      isIdentity: false,
    }));
  } catch (error) {
    console.error('[SQLite] Failed to get column metadata:', error);
    return [];
  }
}

/**
 * Fetch rows from a SQLite table with filtering, sorting, and pagination
 */
export async function fetchTableRows(
  request: TableExplorerFetchRowsRequest
): Promise<TableExplorerFetchRowsResult> {
  if (!isDatabaseOpen()) {
    return {
      success: false,
      error: 'No database connected. Please connect to a SQLite database first.',
      rows: [],
      totalRowCount: 0,
    };
  }

  const db = getCurrentDatabase();
  if (!db) {
    return {
      success: false,
      error: 'Database connection lost.',
      rows: [],
      totalRowCount: 0,
    };
  }

  const { table, columns, limit, offset = 0, filters, sorting } = request;
  const tableName = table.tableName;

  try {
    // Get column metadata
    const columnMetadata = getColumnMetadata(tableName);

    // Build column list
    const columnList =
      columns === '*'
        ? '*'
        : columns.map(escapeIdentifier).join(', ');

    // Build WHERE clause
    const whereClause = buildWhereClause(filters);

    // Build ORDER BY clause
    const orderByClause = buildOrderByClause(sorting);

    // Get total count first (for pagination)
    const countSql = `SELECT COUNT(*) as cnt FROM ${escapeIdentifier(tableName)} ${whereClause}`;
    const countResult = db.exec(countSql);
    const totalRowCount =
      countResult.length > 0 && countResult[0].values.length > 0
        ? Number(countResult[0].values[0][0])
        : 0;

    // Build and execute the main query
    const dataSql = `
      SELECT ${columnList}
      FROM ${escapeIdentifier(tableName)}
      ${whereClause}
      ${orderByClause}
      LIMIT ${limit}
      OFFSET ${offset}
    `.trim();

    const dataResult = db.exec(dataSql);

    if (dataResult.length === 0) {
      // No results - table might be empty
      return {
        success: true,
        rows: [],
        columns: columnMetadata,
        totalRowCount,
      };
    }

    const result = dataResult[0];

    // Build column metadata from query result
    const resultColumns: TableExplorerColumn[] = result.columns.map((name) => {
      const meta = columnMetadata.find(
        (c) => c.name.toLowerCase() === name.toLowerCase()
      );
      return (
        meta || {
          name,
          dataType: 'TEXT',
          nullable: true,
          isPrimaryKey: false,
          isForeignKey: false,
        }
      );
    });

    // Convert rows to Record<string, unknown> format
    const rows: Record<string, unknown>[] = result.values.map((valueRow) => {
      const row: Record<string, unknown> = {};
      for (let i = 0; i < result.columns.length; i++) {
        row[result.columns[i]] = valueRow[i];
      }
      return row;
    });

    return {
      success: true,
      rows,
      columns: resultColumns,
      totalRowCount,
    };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to fetch rows';
    console.error('[SQLite] fetchTableRows error:', error);
    return {
      success: false,
      error: message,
      rows: [],
      totalRowCount: 0,
    };
  }
}

/**
 * Get table metadata (columns) for browser mode SQLite
 */
export function getTableMetadataSqlite(tableName: string): {
  success: boolean;
  columns?: TableExplorerColumn[];
  error?: string;
} {
  if (!isDatabaseOpen()) {
    return {
      success: false,
      error: 'No database connected.',
    };
  }

  const columns = getColumnMetadata(tableName);
  if (columns.length === 0) {
    return {
      success: false,
      error: `Table "${tableName}" not found or has no columns.`,
    };
  }

  return {
    success: true,
    columns,
  };
}

// ============================================================================
// Edit Mode Support (previewChanges, applyChanges)
// ============================================================================

/**
 * Escape a value for SQLite SQL insertion
 */
function escapeValue(value: unknown): string {
  if (value === null || value === undefined) {
    return 'NULL';
  }

  if (typeof value === 'number') {
    if (!Number.isFinite(value)) {
      return 'NULL'; // Handle NaN, Infinity
    }
    return String(value);
  }

  if (typeof value === 'boolean') {
    return value ? '1' : '0';
  }

  if (value instanceof Date) {
    // ISO format for dates
    return `'${value.toISOString()}'`;
  }

  if (typeof value === 'object') {
    // JSON stringify for objects/arrays
    return `'${JSON.stringify(value).replace(/'/g, "''")}'`;
  }

  // String - escape single quotes
  return `'${String(value).replace(/'/g, "''")}'`;
}

/**
 * Build qualified table name for SQLite
 * In SQLite, schema is typically empty or 'main'
 */
function buildQualifiedTableName(schema: string, tableName: string): string {
  // SQLite doesn't use schema prefixes in the same way as SQL Server
  // 'main' is the default schema and can be omitted
  if (schema && schema !== 'main') {
    return `${escapeIdentifier(schema)}.${escapeIdentifier(tableName)}`;
  }
  return escapeIdentifier(tableName);
}

/**
 * Build WHERE clause from primary key values
 */
function buildPrimaryKeyWhereClause(primaryKey: Record<string, unknown>): string {
  const conditions = Object.entries(primaryKey).map(([column, value]) => {
    if (value === null || value === undefined) {
      return `${escapeIdentifier(column)} IS NULL`;
    }
    return `${escapeIdentifier(column)} = ${escapeValue(value)}`;
  });

  return conditions.join(' AND ');
}

/**
 * Generate INSERT SQL for a pending insert
 */
function generateInsertSql(
  schema: string,
  tableName: string,
  insert: PendingInsert
): string {
  const qualifiedName = buildQualifiedTableName(schema, tableName);
  const entries = Object.entries(insert.row).filter(
    ([, value]) => value !== undefined
  );

  if (entries.length === 0) {
    return `INSERT INTO ${qualifiedName} DEFAULT VALUES;`;
  }

  const columns = entries.map(([col]) => escapeIdentifier(col)).join(', ');
  const values = entries.map(([, value]) => escapeValue(value)).join(', ');

  return `INSERT INTO ${qualifiedName} (${columns}) VALUES (${values});`;
}

/**
 * Generate UPDATE SQL for a pending update
 */
function generateUpdateSql(
  schema: string,
  tableName: string,
  update: PendingUpdate
): string {
  const qualifiedName = buildQualifiedTableName(schema, tableName);
  const whereClause = buildPrimaryKeyWhereClause(update.primaryKey);

  return `UPDATE ${qualifiedName} SET ${escapeIdentifier(update.field)} = ${escapeValue(update.newValue)} WHERE ${whereClause};`;
}

/**
 * Generate DELETE SQL for a pending delete
 */
function generateDeleteSql(
  schema: string,
  tableName: string,
  del: PendingDelete
): string {
  const qualifiedName = buildQualifiedTableName(schema, tableName);
  const whereClause = buildPrimaryKeyWhereClause(del.primaryKey);

  return `DELETE FROM ${qualifiedName} WHERE ${whereClause};`;
}

/**
 * Generate SQL for a single pending change
 */
function generateChangeSql(
  schema: string,
  tableName: string,
  change: PendingChange
): string {
  switch (change.type) {
    case 'insert':
      return generateInsertSql(schema, tableName, change);
    case 'update':
      return generateUpdateSql(schema, tableName, change);
    case 'delete':
      return generateDeleteSql(schema, tableName, change);
    default: {
      const _exhaustiveCheck: never = change;
      throw new Error(`Unknown change type: ${(_exhaustiveCheck as PendingChange).type}`);
    }
  }
}

/**
 * Preview changes by generating SQL statements without executing them.
 */
export async function previewChanges(
  request: TableExplorerPreviewChangesRequest
): Promise<TableExplorerPreviewChangesResult> {
  try {
    const { table, changes } = request;

    if (changes.length === 0) {
      return {
        success: true,
        sql: '-- No changes to apply',
      };
    }

    const statements = changes.map((change) =>
      generateChangeSql(table.schema, table.tableName, change)
    );

    const sql = statements.join('\n');

    return {
      success: true,
      sql,
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to generate SQL preview',
    };
  }
}

/**
 * Apply changes by executing generated SQL statements.
 */
export async function applyChanges(
  request: TableExplorerApplyChangesRequest
): Promise<TableExplorerApplyChangesResult> {
  try {
    const { table, changes } = request;

    if (changes.length === 0) {
      return { success: true };
    }

    const db = getCurrentDatabase();
    if (!db) {
      return {
        success: false,
        error: 'No SQLite database is currently open',
      };
    }

    // Generate all SQL statements
    const statements = changes.map((change) =>
      generateChangeSql(table.schema, table.tableName, change)
    );

    // Execute all statements in a transaction
    try {
      db.run('BEGIN TRANSACTION;');

      for (const sql of statements) {
        db.run(sql);
      }

      db.run('COMMIT;');
    } catch (error) {
      // Rollback on error
      try {
        db.run('ROLLBACK;');
      } catch {
        // Ignore rollback errors
      }

      throw error;
    }

    // Checkpoint after successful write (per existing checkpoint policy)
    const databaseId = getCurrentDatabaseId();
    if (databaseId) {
      const exported = exportCurrentDatabase();
      // Only checkpoint if DB is <= 25MB (per checkpoint policy)
      if (exported && exported.byteLength <= AUTO_CHECKPOINT_THRESHOLD) {
        await checkpointDatabase(databaseId, exported);
      }
    }

    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to apply changes',
    };
  }
}

// ============================================================================
// Inbound Foreign Key Explorer
// ============================================================================

/**
 * Fetch inbound foreign keys (tables that reference the target table) for SQLite in browser mode.
 */
export async function fetchInboundForeignKeys(
  request: TableExplorerFetchInboundForeignKeysRequest
): Promise<TableExplorerFetchInboundForeignKeysResult> {
  if (!isDatabaseOpen()) {
    return {
      success: false,
      error: 'No database connected. Please connect to a SQLite database first.',
      foreignKeys: [],
    };
  }

  const db = getCurrentDatabase();
  if (!db) {
    return {
      success: false,
      error: 'Database connection lost.',
      foreignKeys: [],
    };
  }

  const targetTable = request.table.tableName;

  try {
    // Get all tables (excluding system tables)
    const tablesResult = db.exec(
      `SELECT name FROM sqlite_master WHERE type = 'table' AND name NOT LIKE 'sqlite_%'`
    );

    if (tablesResult.length === 0 || tablesResult[0].values.length === 0) {
      return { success: true, foreignKeys: [] };
    }

    const tables = tablesResult[0].values.map((row) => row[0] as string);
    const fkMap = new Map<string, TableExplorerInboundForeignKey>();

    for (const childTable of tables) {
      if (childTable === targetTable) continue; // Skip self

      const escapedName = childTable.replace(/"/g, '""');
      const fkResult = db.exec(`PRAGMA foreign_key_list("${escapedName}")`);

      if (fkResult.length === 0 || fkResult[0].values.length === 0) {
        continue;
      }

      const columns = fkResult[0].columns;
      const idIdx = columns.indexOf('id');
      const tableIdx = columns.indexOf('table');
      const fromIdx = columns.indexOf('from');
      const toIdx = columns.indexOf('to');
      const rows = fkResult[0].values;

      for (const row of rows) {
        const referencedTable = row[tableIdx] as string;

        // Check if this FK references our target table
        if (referencedTable.toLowerCase() !== targetTable.toLowerCase()) continue;

        const fkId = row[idIdx] as number;
        const fromColumn = row[fromIdx] as string;
        const toColumn = row[toIdx] as string;

        // Create synthetic constraint name: fk_{childTable}_{id}
        const constraintName = `fk_${childTable}_${fkId}`;
        const key = `${constraintName}|${childTable}`;

        const existing = fkMap.get(key);
        if (!existing) {
          fkMap.set(key, {
            constraintName,
            referencingSchema: '', // SQLite has no schemas
            referencingTable: childTable,
            referencingColumns: [fromColumn],
            referencedSchema: '',
            referencedTable: referencedTable,
            referencedColumns: [toColumn],
          });
        } else {
          // Composite FK - add additional columns
          existing.referencingColumns.push(fromColumn);
          existing.referencedColumns.push(toColumn);
        }
      }
    }

    return {
      success: true,
      foreignKeys: Array.from(fkMap.values()),
    };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to fetch inbound foreign keys';
    console.error('[SQLite] fetchInboundForeignKeys error:', error);
    return {
      success: false,
      error: message,
      foreignKeys: [],
    };
  }
}

// Export helpers for testing
export const _testHelpers = {
  escapeIdentifier,
  escapeString,
  escapeValue,
  buildQualifiedTableName,
  buildPrimaryKeyWhereClause,
  buildSelectSql,
  buildWhereClause,
  buildOrderByClause,
  generateInsertSql,
  generateUpdateSql,
  generateDeleteSql,
  generateChangeSql,
};
